import React from 'react';
import { bool, func, object, oneOf, string, arrayOf, shape, number, node } from 'prop-types';
import { some } from 'lodash';
import classnames from 'classnames';
import { getContext } from '@fiverr-private/fiverr_context';
import { Container, Stack } from '@fiverr-private/layout_components';
import { CARD_VARIANT, CTAS } from '../../../shared/constants';
import { cardVariantType, sellerType } from '../../../shared/types';
import { highlightsDataType, highlightsType } from '../../SellerHighlight/types';
import SellerHighlight from '../../SellerHighlight';
import ContactButton from '../CardActions/ContactButton';
import CustomButton from '../CardActions/CustomButton';
import { SaveExpertButton } from '../CardActions/SaveExpertButton';
import SeeProfileButton from '../CardActions/SeeProfileButton';
import { hasHighlights } from '../utils';
import CustomerProtection from './CustomerProtection';
import { Price } from './price';
import { smallFooterHighlights } from './Footer.ve.css';

import '../../SellerHighlight/index.scss';
import './index.scss';

const Footer = ({
    priceInUSD,
    highlights,
    highlightsData,
    showPrice = true,
    showSeeProfile: propShowSeeProfile = true,
    showContact: propShowContact,
    showSaveExpert,
    mainCta = CTAS.CONTACT,
    renderContent,
    seller,
    cardVariant,
    currency,
    source,
    footerCustomButtons = [],
    inProCatalogGridExp,
    showHighlightsInSmallCard = false,
    hourlyRateInCents,
    showHourlyPrice = false,
    shouldHideDivider = false,
}) => {
    const {
        experience: { isBusiness },
    } = getContext();

    // If a button is the main CTA, show it by default
    const showContact = propShowContact ?? mainCta === CTAS.CONTACT;
    const showSeeProfile = propShowSeeProfile ?? mainCta === CTAS.SEE_PROFILE;
    const shouldShowPrice = priceInUSD && showPrice && cardVariant !== CARD_VARIANT.SERVICE;
    const shouldShowSmallFooter = shouldShowPrice || showContact || showSaveExpert || showSeeProfile;
    const shouldShowNormalFooter = shouldShowPrice || some(Object.values(highlights));
    const shouldShowHighlights = hasHighlights(highlights);
    const shouldShowHighlightsInSmallFooter =
        (inProCatalogGridExp || showHighlightsInSmallCard) && shouldShowHighlights;
    const shouldShowDivider =
        inProCatalogGridExp || showHighlightsInSmallCard ? !shouldShowHighlights : cardVariant !== CARD_VARIANT.VISUAL;
    const shouldShowCustomerProtection = isBusiness && seller.isPro;

    const preventClickPropagation = (event) => {
        event.stopPropagation();
    };

    const isHourlyPrice = showHourlyPrice && !!hourlyRateInCents;

    return (
        <>
            {shouldShowSmallFooter && (
                <div
                    className={classnames('card-footer-small', {
                        [`m-t-36 divider`]: shouldShowDivider,
                        'no-divider': shouldHideDivider,
                        'p-t-16': !shouldShowHighlightsInSmallFooter,
                    })}
                >
                    {shouldShowHighlightsInSmallFooter && (
                        <Container paddingY="6" className={smallFooterHighlights}>
                            <SellerHighlight highlights={highlights} highlightsData={highlightsData} />
                        </Container>
                    )}
                    <div className="flex flex-justify-between flex-items-center">
                        {renderContent
                            ? renderContent({ seller })
                            : shouldShowPrice && (
                                  <Stack direction="column" gap="0">
                                      <Price
                                          isHourlyPrice={isHourlyPrice}
                                          priceInUSD={isHourlyPrice ? hourlyRateInCents / 100 : priceInUSD}
                                          currency={currency}
                                          containerClassName="m-l-0 p-r-12"
                                      />
                                      {!inProCatalogGridExp && shouldShowCustomerProtection && !isHourlyPrice && (
                                          <CustomerProtection source={source} />
                                      )}
                                  </Stack>
                              )}
                        {
                            <div
                                className="flex flex-justify-end listings-action-menu-container"
                                onClick={preventClickPropagation}
                            >
                                {showContact && (
                                    <ContactButton
                                        source={source}
                                        className="m-r-0"
                                        size="sm"
                                        isCta={mainCta === CTAS.CONTACT}
                                    />
                                )}
                                {showSaveExpert && <SaveExpertButton size="sm" />}
                                {showSeeProfile && (
                                    <SeeProfileButton source={source} isCta={mainCta === CTAS.SEE_PROFILE} size="sm" />
                                )}
                                {footerCustomButtons.map(
                                    ({
                                        key,
                                        label,
                                        onClick,
                                        isCta,
                                        elementName,
                                        loading,
                                        disabled,
                                        variant,
                                        icon,
                                        style,
                                    }) => (
                                        <CustomButton
                                            key={key || label}
                                            style={style}
                                            label={label}
                                            onClick={onClick}
                                            isCta={isCta}
                                            source={source}
                                            elementName={elementName}
                                            size="sm"
                                            loading={loading}
                                            disabled={disabled}
                                            variant={variant}
                                            icon={icon}
                                        />
                                    )
                                )}
                            </div>
                        }
                    </div>
                </div>
            )}
            {shouldShowNormalFooter &&
                (renderContent ? (
                    <div className="card-footer divider flex flex-justify-between p-t-20 m-t-16">
                        {renderContent({ seller })}
                    </div>
                ) : (
                    <div
                        className={classnames('card-footer flex flex-justify-between m-t-24', {
                            [`p-t-24 divider`]: shouldShowDivider,
                        })}
                    >
                        <SellerHighlight highlights={highlights} highlightsData={highlightsData} />
                        {shouldShowPrice && (
                            <Stack direction="column" gap="0">
                                <Price
                                    isHourlyPrice={isHourlyPrice}
                                    priceInUSD={isHourlyPrice ? hourlyRateInCents / 100 : priceInUSD}
                                    currency={currency}
                                    containerClassName="m-l-0 p-r-12"
                                />
                                {shouldShowCustomerProtection && !isHourlyPrice && (
                                    <CustomerProtection source={source} />
                                )}
                            </Stack>
                        )}
                    </div>
                ))}
        </>
    );
};

Footer.propTypes = {
    priceInUSD: string.isRequired,
    highlights: highlightsType.isRequired,
    highlightsData: highlightsDataType.isRequired,
    showPrice: bool,
    showSaveExpert: bool,
    showContact: bool,
    showSeeProfile: bool,
    cardVariant: cardVariantType,
    currency: object,
    source: string,
    mainCta: oneOf(Object.values(CTAS)),
    seller: sellerType,
    renderContent: func,
    footerCustomButtons: arrayOf(
        shape({
            key: string,
            label: string,
            onClick: func,
            isCta: bool,
            elementName: string,
            loading: bool,
            disabled: bool,
            variant: string,
            icon: node,
            style: object,
        })
    ),
    inProCatalogGridExp: bool,
    showHighlightsInSmallCard: bool,
    hourlyRateInCents: number,
    showHourlyPrice: bool,
    shouldHideDivider: bool,
};

export default Footer;
